import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SigningSession, SigningSessionStatus } from '@app/models/signing-session/signing-session';
import { SigningAuthenticatorProxyService } from '@app/proxies/signing-authenticator.proxy.service';
import { SigningSessionProxyService } from '@app/proxies/signing-session.proxy.service';
import { Observable, catchError, map, of, switchMap, tap, throwError } from 'rxjs';

// Countdowns are driven by the server's SecondsUntil* values plus a monotonic clock, never by
// comparing server timestamps with the browser's clock, so a skewed PC clock can't lie about expiry.
@Injectable({ providedIn: 'root' })
export class SigningSessionStateService {
  private readonly proxy = inject(SigningSessionProxyService);
  private readonly authenticatorProxy = inject(SigningAuthenticatorProxyService);
  private readonly translate = inject(TranslateService);

  readonly session = signal<SigningSession | null>(null);
  readonly lastEnded = signal<SigningSession | null>(null);
  readonly loaded = signal(false);

  private readonly syncedAt = signal(performance.now());
  private readonly now = signal(performance.now());
  private timer: ReturnType<typeof setInterval> | null = null;
  private refreshing = false;

  private readonly secondsSinceSync = computed(() => Math.max(0, Math.floor((this.now() - this.syncedAt()) / 1000)));

  readonly isActive = computed(() => !!this.session()?.canSign);

  readonly isPendingVerification = computed(() => {
    const session = this.session();
    return !!session && session.status === SigningSessionStatus.Open && !session.isTerminal;
  });

  readonly elapsedSeconds = computed(() => {
    const session = this.session();
    if (!session) return 0;
    return session.hardCapSeconds - session.secondsUntilHardExpiry + this.secondsSinceSync();
  });

  readonly secondsUntilExpiry = computed(() => {
    const session = this.session();
    if (!session) return 0;
    return Math.max(0, Math.min(session.secondsUntilIdleExpiry, session.secondsUntilHardExpiry) - this.secondsSinceSync());
  });

  readonly otpSecondsLeft = computed(() => {
    const otp = this.session()?.otp;
    if (!otp?.isPending) return 0;
    return Math.max(0, otp.secondsUntilExpiry - this.secondsSinceSync());
  });

  /** Null until the API has answered; false means the person still has to set up their app. */
  readonly authenticatorConfigured = signal<boolean | null>(null);

  constructor() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.loaded()) this.refreshSilently();
    });
  }

  ensureLoaded(): void {
    if (!this.loaded()) this.refreshSilently();
    if (this.authenticatorConfigured() === null) this.refreshAuthenticator();
  }

  refreshAuthenticator(): void {
    this.authenticatorProxy.getStatus().subscribe({
      next: (response) => this.authenticatorConfigured.set(!!response.data?.isConfigured),
      error: () => this.authenticatorConfigured.set(false),
    });
  }

  refresh(): Observable<SigningSession | null> {
    return this.proxy.getCurrent().pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
      tap((session) => {
        this.setSession(session);
        this.loaded.set(true);
      }),
    );
  }

  openOrResume(): Observable<SigningSession> {
    const current = this.session();
    if (current && this.isPendingVerification()) return of(current);

    this.lastEnded.set(null);
    return this.proxy.open().pipe(
      map((response) => response.data as SigningSession),
      tap((session) => this.setSession(session)),
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 409) return throwError(() => error);
        return this.refresh().pipe(
          switchMap((session) =>
            session && session.status === SigningSessionStatus.Open && !session.isTerminal ? of(session) : throwError(() => error),
          ),
        );
      }),
    );
  }

  verify(code: string): Observable<SigningSession> {
    const id = this.requireId();
    return this.proxy.verifyOtp(id, code).pipe(
      map((response) => response.data as SigningSession),
      tap((session) => this.setSession(session)),
      catchError((error) => this.reloadAfterFailure(id).pipe(switchMap(() => throwError(() => error)))),
    );
  }

  close(): Observable<SigningSession | null> {
    const id = this.requireId();
    return this.proxy.close(id).pipe(
      map((response) => response.data ?? null),
      tap(() => this.setSession(null)),
    );
  }

  messageFrom(error: unknown): string {
    const message = (error as HttpErrorResponse)?.error?.errorMessage as Record<string, string> | null | undefined;
    const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    return message?.[lang] || message?.['en'] || this.translate.instant('Signing_Error_Generic');
  }

  private reloadAfterFailure(id: string): Observable<SigningSession | null> {
    return this.proxy.getById(id).pipe(
      map((response) => response.data ?? null),
      tap((session) => {
        if (session?.isTerminal) {
          this.lastEnded.set(session);
          this.setSession(null);
        } else {
          this.setSession(session);
        }
      }),
      catchError(() => of(null)),
    );
  }

  private refreshSilently(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.refresh().subscribe({
      next: () => (this.refreshing = false),
      error: () => (this.refreshing = false),
    });
  }

  private requireId(): string {
    const id = this.session()?.id;
    if (!id) throw new Error('There is no signing session to act on.');
    return id;
  }

  private setSession(session: SigningSession | null): void {
    this.session.set(session);
    this.syncedAt.set(performance.now());
    this.now.set(performance.now());

    if (session && !this.timer) {
      this.timer = setInterval(() => this.tick(), 1000);
    } else if (!session && this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private tick(): void {
    this.now.set(performance.now());
    if (this.session() && this.secondsUntilExpiry() === 0) this.refreshSilently();
  }
}
